import { Form } from '@epages/react-components'
import type { ReactElement } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import Immutable from 'immutable'

import { CategoryIdSettings } from './CategoryIdSettings'
import { listCategories } from '../../../../../store/actions'
import { useTranslate } from '../../../../../utils/translate'
import Button from '../../../../formComponents/Button'
import ButtonRow from '../../../../formComponents/ButtonRow'
import CategoryProductSizeSettings from './CategoryProductSize'

const tabs: { [key: string]: (props: any) => ReactElement } = {
  collectionIdSettings: CategoryIdSettings,
  collectionProductSizeSettings: CategoryProductSizeSettings,
}

const formatPageSize =
  (callback: (data: ImmutableMap) => void) =>
  (data: ImmutableMap): void => {
    callback(data.set('pageSize', data.get('imageSize') === 'S' ? 15 : 12))
  }

export default function CategorySettings({
  data,
  onCancel,
  onSave,
  onDataChange,
  renderLayout,
}: WorkspacePluginSettingsProps): ReactElement {
  const t = useTranslate('interface')
  const scopedT = (key: string) => t(`components.collectionComponent.settingsModal.${key}`)
  const isBeyond = useSelector<State, boolean>((state) => Boolean(state.getIn(['shop', 'beyond'])))

  const dispatch = useDispatch<GlobalDispatch>()
  useEffect(() => {
    if (isBeyond) dispatch(listCategories())
  }, [dispatch, isBeyond])

  const categories = useSelector<State, ImmutableMap>((state) => state.get('categoriesList'))
  const categoryOptions = categories.size
    ? categories
        .valueSeq()
        .map((category: ImmutableMap) => {
          const label = category.get('type')
            ? `${category.get('name')} (${category.get('type')})`
            : category.get('name')

          return {
            value: category.get('id'),
            label,
          }
        })
        .toJS()
    : null

  const validateForm = (data: ImmutableMap) =>
    Immutable.fromJS({
      categoryId: isBeyond ? !data.get('categoryId') && scopedT('collectionDropdown.validationMessage') : null,
    })

  const formData = data
    .set('imageSize', data.get('imageSize', 'M'))
    // Set the selected categoryId to the defaultOptions value.
    // The settings layer does not render properly when a dropdown value is selected
    // that is not part of the options.
    .set('categoryId', categoryOptions ? data.get('categoryId') : '')

  if (categoryOptions && !data.get('categoryId')) {
    categoryOptions.unshift({
      value: '',
      label: scopedT('collectionDropdown.defaultOption'),
    })
  }

  const [activeTab, setActiveTab] = useState(isBeyond ? Object.keys(tabs)[0] : 'collectionProductSizeSettings')
  const Setting = tabs[activeTab]

  return (
    <Form
      name="pluginCategory"
      value={formData}
      validate={validateForm}
      onSubmit={formatPageSize(onSave)}
      onChange={formatPageSize(onDataChange)}
    >
      {renderLayout({
        title:
          activeTab === Object.keys(tabs)[0]
            ? data.get('categoryId')
              ? scopedT('title.onEdit')
              : scopedT('title.onCreate')
            : scopedT(`${activeTab}.title`),
        tabs: Object.keys(tabs)
          .filter((tab) => isBeyond || tab === 'collectionProductSizeSettings')
          .map((tab) => ({
            name: tab,
            title: scopedT(`${tab}.title`),
            active: activeTab === tab,
            onClick: (name) => setActiveTab(name),
          })),
        body: <Setting t={scopedT} categoryOptions={categoryOptions} />,
        footer: (
          <ButtonRow>
            <Button variant="cancel" type="button" onClick={onCancel}>
              {scopedT('cancelButton.label')}
            </Button>
            <Button>{scopedT('saveButton.label')}</Button>
          </ButtonRow>
        ),
      })}
    </Form>
  )
}
